import React, { useState, useEffect } from 'react';
import { Building2, Store, Scissors, ChevronLeft, ChevronRight } from 'lucide-react';
import { businessesData } from '../mock';

const ImageCarousel = ({ images, alt }) => {
  const [currentImage, setCurrentImage] = useState(0);

  // Auto-rotate every 5 seconds
  useEffect(() => {
    if (images && images.length > 1) {
      const interval = setInterval(() => {
        setCurrentImage((prev) => (prev + 1) % images.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [images]);

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + images.length) % images.length);
  };

  if (!images || images.length === 0) return null;
  
  // Handle both string URLs and object formats
  const getImageUrl = (img) => typeof img === 'string' ? img : img.url;
  const getImagePosition = (img) => typeof img === 'string' ? 'center center' : (img.position || 'center center');
  
  if (images.length === 1) {
    return <img src={getImageUrl(images[0])} alt={alt} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: getImagePosition(images[0]) }} />;
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <img 
        src={getImageUrl(images[currentImage])} 
        alt={`${alt} - ${currentImage + 1}`} 
        style={{ 
          width: '100%', 
          height: '100%', 
          objectFit: 'cover',
          objectPosition: getImagePosition(images[currentImage]),
          transition: 'opacity 0.3s ease'
        }} 
      />
      
      {/* Navigation Arrows */}
      <button
        onClick={prevImage}
        style={{
          position: 'absolute',
          left: '16px',
          top: '50%',
          transform: 'translateY(-50%)',
          background: 'rgba(0, 0, 0, 0.5)',
          border: 'none',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          zIndex: 10
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(212, 175, 55, 0.9)'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.5)'}
      >
        <ChevronLeft size={24} color="white" />
      </button>
      
      <button
        onClick={nextImage}
        style={{
          position: 'absolute',
          right: '16px',
          top: '50%',
          transform: 'translateY(-50%)',
          background: 'rgba(0, 0, 0, 0.5)',
          border: 'none',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          zIndex: 10
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(212, 175, 55, 0.9)'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.5)'}
      >
        <ChevronRight size={24} color="white" />
      </button>
      
      {/* Dot Indicators */}
      <div style={{ 
        position: 'absolute', 
        bottom: '16px', 
        left: '50%', 
        transform: 'translateX(-50%)',
        display: 'flex',
        gap: '8px'
      }}>
        {images.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentImage(index)}
            style={{
              width: currentImage === index ? '24px' : '8px',
              height: '8px',
              borderRadius: '4px',
              background: currentImage === index ? 'var(--accent-gold)' : 'rgba(255, 255, 255, 0.5)',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            aria-label={`Go to image ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export const Businesses = () => {
  const [visibleSections, setVisibleSections] = useState([]);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const id = entry.target.id;
          setVisibleSections((prev) => [...new Set([...prev, id])]);
        }
      });
    }, { threshold: 0.1 });

    const sections = document.querySelectorAll('.business-section');
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="businesses" className="section-padding" style={{ background: 'var(--bg-primary)' }}>
      <div className="container">
        <div style={{ textAlign: 'center', marginBottom: '80px' }}>
          <h2 className="heading-1">Core competencies</h2>
          <div className="gold-line" style={{ margin: '24px auto' }} />
        </div>

        {/* Retail and Apparel */}
        <div id="retail" className="business-section" style={{ marginBottom: '120px', opacity: visibleSections.includes('retail') ? 1 : 0, transform: visibleSections.includes('retail') ? 'translateY(0)' : 'translateY(40px)', transition: 'all 0.8s ease' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '60px', alignItems: 'center' }}>
            <div>
              <div style={{ width: '60px', height: '60px', border: '2px solid var(--accent-gold)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px' }}>
                <Store size={32} color="var(--accent-gold)" />
              </div>
              <h3 className="heading-2" style={{ marginBottom: '16px' }}>{businessesData.retail.title}</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>{businessesData.retail.description}</p>
              {businessesData.retail.highlights.length > 0 && (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {businessesData.retail.highlights.map((item, idx) => (
                    <li key={idx} className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '10px', paddingLeft: '20px', position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 0, color: 'var(--accent-gold)' }}>•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div style={{ background: 'var(--bg-secondary)', height: '400px', overflow: 'hidden', borderRadius: '12px' }}>
              <ImageCarousel images={businessesData.retail.images} alt="YFT Retail storefront" />
            </div>
          </div>
        </div>

        {/* Hair and Beauty */}
        <div id="beauty" className="business-section" style={{ marginBottom: '120px', opacity: visibleSections.includes('beauty') ? 1 : 0, transform: visibleSections.includes('beauty') ? 'translateY(0)' : 'translateY(40px)', transition: 'all 0.8s ease' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '60px', alignItems: 'center' }}>
            <div style={{ background: 'var(--bg-secondary)', height: '400px', overflow: 'hidden', borderRadius: '12px', order: window.innerWidth < 768 ? 2 : 1 }}>
              <ImageCarousel images={businessesData.beauty.images} alt="Crossover Bollywood Salon" />
            </div>
            <div style={{ order: window.innerWidth < 768 ? 1 : 2 }}>
              <div style={{ width: '60px', height: '60px', border: '2px solid var(--accent-gold)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px' }}>
                <Scissors size={32} color="var(--accent-gold)" />
              </div>
              <h3 className="heading-2" style={{ marginBottom: '16px' }}>{businessesData.beauty.title}</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>{businessesData.beauty.description}</p>
              {businessesData.beauty.highlights.length > 0 && (
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px 0' }}>
                  {businessesData.beauty.highlights.map((item, idx) => (
                    <li key={idx} className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '10px', paddingLeft: '20px', position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 0, color: 'var(--accent-gold)' }}>•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              )}
              <a href={businessesData.beauty.websiteUrl} target="_blank" rel="noopener noreferrer" className="btn-primary button-text">
                {businessesData.beauty.websiteText}
              </a>
            </div>
          </div>
        </div>

        {/* Additional Investments */}
        <div id="investments" className="business-section" style={{ opacity: visibleSections.includes('investments') ? 1 : 0, transform: visibleSections.includes('investments') ? 'translateY(0)' : 'translateY(40px)', transition: 'all 0.8s ease' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '60px', alignItems: 'center' }}>
            <div>
              <div style={{ width: '60px', height: '60px', border: '2px solid var(--accent-gold)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px' }}>
                <Building2 size={32} color="var(--accent-gold)" />
              </div>
              <h3 className="heading-2" style={{ marginBottom: '16px' }}>{businessesData.additionalInvestments.title}</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>{businessesData.additionalInvestments.description}</p>
              {businessesData.additionalInvestments.highlights.length > 0 && (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {businessesData.additionalInvestments.highlights.map((item, idx) => (
                    <li key={idx} className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '10px', paddingLeft: '20px', position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 0, color: 'var(--accent-gold)' }}>•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div style={{ background: 'var(--bg-secondary)', height: '400px', overflow: 'hidden', borderRadius: '12px' }}>
              <ImageCarousel images={businessesData.additionalInvestments.images} alt="YFT Additional Investments" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
